// components/analytics/components/charts/WaterfallChart.tsx
import { Column, ColumnOptions } from '@antv/g2plot';
import { useEffect, useRef } from 'react';
import { Skeleton } from 'antd';
import NoDataPlaceholder from '../NoDataPlaceholder';

interface DataItem {
  [key: string]: any;
  total?: number;
  color?: string;
}

interface WaterfallChartProps {
  data: DataItem[];
  categoryField: string;
  valueField: string;
  isTotalField: string;
  height?: number;
  loading?: boolean;
}

export const WaterfallChart: React.FC<WaterfallChartProps> = ({
  data,
  categoryField,
  valueField,
  isTotalField,
  height = 300,
  loading = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Column | null>(null);

  useEffect(() => {
    if (!containerRef.current || !data?.length) {
      return;
    }

    try {
      // Clean up previous chart instance
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      // Process data with strict typing
      let total = 0;
      const processedData: DataItem[] = data.map(item => {
        if (!item[isTotalField]) {
          total += Number(item[valueField]) || 0;
        }
        return {
          ...item,
          total,
          color: item[isTotalField] 
            ? '#333' 
            : (Number(item[valueField]) >= 0 ? '#3f8600' : '#cf1322')
        };
      });

      // Create new chart instance with strict configuration
      const chart = new Column(containerRef.current, {
        data: processedData,
        xField: categoryField,
        yField: valueField,
        seriesField: 'color',
        label: {
          position: 'middle',
          formatter: (v: DataItem) => {
            const value = Number(v[valueField]);
            return `${value >= 0 ? '+' : ''}${value}`;
          }
        },
        columnStyle: (datum: DataItem) => ({
          fill: datum[isTotalField] ? '#333' : (Number(datum[valueField]) >= 0 ? '#3f8600' : '#cf1322')
        }),
        animation: {
          appear: {
            animation: 'fade-in',
            duration: 1000
          }
        }
      } as ColumnOptions);

      // Set reference and render
      chartRef.current = chart;
      chart.render();

    } catch (error) {
      console.error('[WaterfallChart] Error:', error);
    }

    // Cleanup function
    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
          chartRef.current = null;
        } catch (error) {
          console.error('[WaterfallChart] Cleanup error:', error);
        }
      }
    };
  }, [data, categoryField, valueField, isTotalField]);

  // Show skeleton while loading
  if (loading) {
    return (
      <div style={{ height, padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  // Check for empty data
  if (!data?.length) {
    return <NoDataPlaceholder height={height} />;
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: `${height}px`,
        overflow: 'hidden',
        borderRadius: '6px'
      }}
    />
  );
};

export default WaterfallChart;